/**
 * Data Layer Utility Functions
 * Shared utility functions for data layer enhancement.
 * Provides reusable functions for enhancing products with categories, condition, and formatted prices.
 *
 * This script can be loaded multiple times safely - it checks if DataLayerUtils already exists.
 */

// Only initialize if DataLayerUtils doesn't already exist
if (
  typeof window === "undefined" ||
  typeof window.DataLayerUtils === "undefined"
) {
  (function () {
    "use strict";

    /**
     * Gets currency code from Shopify global or dataLayer
     */
    function getCurrencyCode() {
      try {
        // Try to get from Shopify global
        if (typeof window !== "undefined" && window.Shopify?.currency?.active) {
          return window.Shopify.currency.active;
        }
        // Try to get from dataLayer in localStorage
        const dataLayer = getDataLayer();
        if (dataLayer?.currency) {
          return typeof dataLayer.currency === "string"
            ? dataLayer.currency
            : dataLayer.currency.code || dataLayer.currency.active;
        }
      } catch (e) {
        // Ignore errors
      }
      return null;
    }

    /**
     * Gets locale from Shopify global or dataLayer
     */
    function getLocale() {
      try {
        // Try to get from Shopify global
        if (typeof window !== "undefined" && window.Shopify?.locale) {
          return window.Shopify.locale;
        }
        // Try to get from dataLayer in localStorage
        const dataLayer = getDataLayer();
        if (dataLayer?.locale) {
          return dataLayer.locale;
        }
      } catch (e) {
        // Ignore errors
      }
      return null;
    }

    /**
     * Gets currency symbol from dataLayer or defaults
     */
    function getCurrencySymbol() {
      try {
        const dataLayer = getDataLayer();
        if (dataLayer?.currency) {
          if (
            typeof dataLayer.currency === "object" &&
            dataLayer.currency.symbol
          ) {
            return dataLayer.currency.symbol;
          }
        }
      } catch (e) {
        // Ignore errors
      }
      return null;
    }

    /**
     * Converts price from cents to decimal
     * @param {number} priceInCents - Price in cents
     * @returns {number|null} - Price in decimal format
     */
    function convertPriceToDecimal(priceInCents) {
      if (priceInCents === null || priceInCents === undefined) {
        return null;
      }
      return priceInCents / 100.0;
    }

    /**
     * Formats a price using Shopify's currency settings
     * @param {number} priceInCents - Price in cents
     * @param {string} currencyCode - Currency code (e.g., 'EUR', 'USD')
     * @param {string} locale - Locale string (e.g., 'en-US', 'de-DE')
     * @param {string} currencySymbol - Currency symbol (e.g., '€', '$')
     * @returns {string|null} - Formatted price string
     */
    function formatPrice(priceInCents, currencyCode, locale, currencySymbol) {
      if (priceInCents === null || priceInCents === undefined) {
        return null;
      }

      const price = priceInCents / 100.0;

      // Use provided currency code/locale or get from globals
      const finalCurrencyCode = currencyCode || getCurrencyCode() || "USD";
      const finalLocale = locale || getLocale() || "en-US";
      const finalCurrencySymbol = currencySymbol || getCurrencySymbol();

      // Try to use Shopify's formatMoney if available
      if (
        typeof window !== "undefined" &&
        window.Shopify?.formatMoney &&
        finalCurrencySymbol
      ) {
        try {
          return window.Shopify.formatMoney(priceInCents, finalCurrencySymbol);
        } catch (e) {
          // Fall through to Intl.NumberFormat
        }
      }

      // Fallback to Intl.NumberFormat
      try {
        return price.toLocaleString(finalLocale, {
          style: "currency",
          currency: finalCurrencyCode,
        });
      } catch (e) {
        // Last resort: simple format
        return price.toFixed(2) + " " + finalCurrencyCode;
      }
    }

    /**
     * Enhances a product object with additional data
     * @param {Object} product - Product object to enhance
     * @param {Object} enhancement - Enhancement data (categories, condition, prices)
     * @returns {Object} - Enhanced product object
     */
    function enhanceProduct(product, enhancement) {
      if (!product || !enhancement) {
        return product;
      }

      const enhanced = {
        ...product,
        categories: enhancement.categories || product.categories || null,
        condition: enhancement.condition || product.condition || null,
      };

      // Add price information if provided
      if (enhancement.price !== null && enhancement.price !== undefined) {
        enhanced.price = enhancement.price;
      }
      if (
        enhancement.price_formatted !== null &&
        enhancement.price_formatted !== undefined
      ) {
        enhanced.price_formatted = enhancement.price_formatted;
      }
      if (
        enhancement.compare_at_price !== null &&
        enhancement.compare_at_price !== undefined
      ) {
        enhanced.compare_at_price = enhancement.compare_at_price;
      }
      if (
        enhancement.compare_at_price_formatted !== null &&
        enhancement.compare_at_price_formatted !== undefined
      ) {
        enhanced.compare_at_price_formatted =
          enhancement.compare_at_price_formatted;
      }

      return enhanced;
    }

    /**
     * Enhances a variant object with price data
     * @param {Object} variant - Variant object to enhance
     * @param {Object} enhancement - Enhancement data (prices)
     * @returns {Object} - Enhanced variant object
     */
    function enhanceVariant(variant, enhancement) {
      if (!variant || !enhancement) {
        return variant;
      }

      const enhanced = {
        ...variant,
      };

      // Add price information if provided
      if (enhancement.price !== null && enhancement.price !== undefined) {
        enhanced.price = enhancement.price;
      }
      if (
        enhancement.price_formatted !== null &&
        enhancement.price_formatted !== undefined
      ) {
        enhanced.price_formatted = enhancement.price_formatted;
      }
      if (
        enhancement.compare_at_price !== null &&
        enhancement.compare_at_price !== undefined
      ) {
        enhanced.compare_at_price = enhancement.compare_at_price;
      }
      if (
        enhancement.compare_at_price_formatted !== null &&
        enhancement.compare_at_price_formatted !== undefined
      ) {
        enhanced.compare_at_price_formatted =
          enhancement.compare_at_price_formatted;
      }

      return enhanced;
    }

    /**
     * Enhances a cart item with product data
     * @param {Object} item - Cart item object to enhance
     * @param {Object} enhancement - Enhancement data
     * @param {string} currencyCode - Currency code
     * @param {string} locale - Locale string
     * @returns {Object} - Enhanced cart item object
     */
    function enhanceCartItem(item, enhancement, currencyCode, locale) {
      if (!item) {
        return item;
      }

      // Calculate numeric prices (cart items use price and original_price)
      const price = convertPriceToDecimal(item.price);
      const originalPrice = convertPriceToDecimal(item.original_price);

      // Format prices
      const priceFormatted = formatPrice(item.price, currencyCode, locale);
      const originalPriceFormatted = formatPrice(
        item.original_price,
        currencyCode,
        locale
      );

      const enhanced = {
        ...item,
        price: price,
        price_formatted: priceFormatted,
        original_price: originalPrice,
        original_price_formatted: originalPriceFormatted,
      };

      // Add enhancement data if available (from product data)
      if (enhancement) {
        if (
          enhancement.categories !== null &&
          enhancement.categories !== undefined
        ) {
          enhanced.categories = enhancement.categories;
        }
        if (
          enhancement.condition !== null &&
          enhancement.condition !== undefined
        ) {
          enhanced.condition = enhancement.condition;
        }

        // Use enhancement prices if provided (from product-level compare_at_price)
        if (enhancement.price !== null && enhancement.price !== undefined) {
          enhanced.price = enhancement.price;
          enhanced.price_formatted = enhancement.price_formatted;
        }
        if (
          enhancement.compare_at_price !== null &&
          enhancement.compare_at_price !== undefined
        ) {
          enhanced.compare_at_price = enhancement.compare_at_price;
          enhanced.compare_at_price_formatted =
            enhancement.compare_at_price_formatted;
        }
      }

      return enhanced;
    }

    /**
     * Gets or creates dataLayer in localStorage
     * @returns {Object} - Existing or new dataLayer object
     */
    function getDataLayer() {
      try {
        const stored = localStorage.getItem("dataLayer");
        if (stored) {
          return JSON.parse(stored);
        }
      } catch (e) {
        // If parsing fails, start fresh
      }
      return {};
    }

    /**
     * Saves dataLayer to localStorage, merging with existing data
     * @param {Object} newData - New data to merge into dataLayer
     */
    function saveDataLayer(newData) {
      try {
        const existingDataLayer = getDataLayer();
        const currency = getCurrencyCode();
        const locale = getLocale();

        const updatedDataLayer = {
          ...existingDataLayer,
          ...newData,
        };

        // Add currency and locale if available
        if (currency) {
          updatedDataLayer.currency = currency;
        }
        if (locale) {
          updatedDataLayer.locale = locale;
        }

        localStorage.setItem("dataLayer", JSON.stringify(updatedDataLayer));
      } catch (error) {
        console.error("Error saving to dataLayer:", error);
      }
    }

    // Create the DataLayerUtils object
    const DataLayerUtils = {
      convertPriceToDecimal,
      formatPrice,
      enhanceProduct,
      enhanceVariant,
      enhanceCartItem,
      getDataLayer,
      saveDataLayer,
    };

    // Export as ES module (if using modules)
    if (typeof module !== "undefined" && module.exports) {
      module.exports = DataLayerUtils;
    }

    // Attach to window for global access (works in both main page and iframe contexts)
    // This must be synchronous and not deferred so it's available immediately
    if (typeof window !== "undefined") {
      window.DataLayerUtils = DataLayerUtils;
    }
  })();
}
